import { WritableDraft } from "immer";

import {
  ConversationAction,
  ConversationHooks,
  ConversationMessage,
  ConversationNode,
  ConversationState,
  PickType,
  UIMessage,
  UIMessagePart,
  UIMessageStreamPart,
} from "@intelligems/ig-types";

import { initialWorkflowConversationState } from "./initial-state";

// ===========================
// Type Guards
// ===========================

const isTextStreamPart = (
  part: UIMessageStreamPart
): part is PickType<UIMessageStreamPart, "text"> => {
  return part.type === "text";
};

const isToolInputDeltaStreamPart = (
  part: UIMessageStreamPart
): part is PickType<UIMessageStreamPart, "tool-input-delta"> => {
  return part.type === "tool-input-delta";
};

const isToolOutputDeltaStreamPart = (
  part: UIMessageStreamPart
): part is PickType<UIMessageStreamPart, "tool-output-available"> => {
  return part.type === "tool-output-available";
};

const isReasoningStreamPart = (
  part: UIMessageStreamPart
): part is PickType<UIMessageStreamPart, "reasoning"> => {
  return part.type === "reasoning";
};

const isStartPart = (
  part: UIMessageStreamPart
): part is PickType<UIMessageStreamPart, "start"> => {
  return part.type === "start";
};

const isFinishPart = (
  part: UIMessageStreamPart
): part is PickType<UIMessageStreamPart, "finish"> => {
  return part.type === "finish";
};

// ===========================
// Stream Concatenation Helpers
// ===========================

/**
 * Concatenates consecutive text stream parts to reduce redundancy
 * @returns The concatenated text if successful, false otherwise
 */
const concatTextStream = (
  streamParts: WritableDraft<UIMessageStreamPart[]>,
  newTextPart: PickType<UIMessageStreamPart, "text">
): string | false => {
  const lastPart = streamParts[streamParts.length - 1];

  if (lastPart && isTextStreamPart(lastPart)) {
    lastPart.text += newTextPart.text;
    return lastPart.text;
  }

  return false;
};

/**
 * Concatenates tool input delta parts for the same tool call
 */
const concatToolInputDeltaStream = (
  streamParts: WritableDraft<UIMessageStreamPart[]>,
  newDeltaPart: PickType<UIMessageStreamPart, "tool-input-delta">
): boolean => {
  const lastPart = streamParts[streamParts.length - 1];

  if (
    lastPart &&
    isToolInputDeltaStreamPart(lastPart) &&
    lastPart.toolCallId === newDeltaPart.toolCallId
  ) {
    lastPart.inputTextDelta += newDeltaPart.inputTextDelta;
    return true;
  }

  return false;
};

/**
 * Updates tool output status for matching tool calls
 */
const updateToolStatus = (
  streamParts: WritableDraft<UIMessageStreamPart[]>,
  part: PickType<UIMessageStreamPart, "tool-output-available">
): boolean => {
  const lastPart = streamParts[streamParts.length - 1];

  if (
    lastPart &&
    isToolOutputDeltaStreamPart(lastPart) &&
    lastPart.toolCallId === part.toolCallId
  ) {
    lastPart.output = part.output;
    return true;
  }

  return false;
};

/**
 * Concatenates reasoning parts from the same node
 */
const concatReasoningStream = (
  streamParts: WritableDraft<UIMessageStreamPart[]>,
  reasoningPart: PickType<UIMessageStreamPart, "reasoning">
): boolean => {
  const lastPart = streamParts[streamParts.length - 1];

  if (
    lastPart &&
    isReasoningStreamPart(lastPart) &&
    lastPart.nodeId === reasoningPart.nodeId
  ) {
    lastPart.text += `\n${reasoningPart.text}`;
    return true;
  }

  return false;
};

// ===========================
// Message Conversion
// ===========================

/**
 * Converts a UIMessage to a ConversationMessage format
 * Handles all supported message part types
 */
const convertUIMessageToConversationMessage = (
  action: Extract<ConversationAction, { type: "addMessage" }>,
  hooks?: ConversationHooks
): ConversationMessage => {
  const streamParts: UIMessageStreamPart[] = action.message.parts.map(
    (part) => {
      const req = {
        timestamp: Date.now(),
        nodeId: action.nodeId,
        messageId: action.messageId,
      };

      switch (part.type) {
        case "text":
          hooks?.onText?.(part.text);
          return {
            ...req,
            type: "text",
            text: part.text,
          };

        case "reasoning":
          return {
            type: "reasoning",
            text: part.text,
            ...req,
          };

        case "source-url":
          return {
            type: "source-url",
            sourceId: part.sourceId,
            url: part.url,
            title: part.title,
            ...req,
          };

        case "source-document":
          return {
            type: "source-document",
            sourceId: part.sourceId,
            mediaType: part.mediaType,
            title: part.title,
            filename: part.filename,
            ...req,
          };

        case "file":
          return {
            type: "file",
            mediaType: part.mediaType,
            url: part.url,
            ...req,
          };

        case "step-start":
          return {
            type: "start-step",
            ...req,
          };

        default:
          // Fallback for any unhandled types
          return {
            type: "text",
            text: "[Unsupported content]",
            ...req,
          };
      }
    }
  );

  return {
    id: action.message.id,
    role: action.message.role,
    timestamp: Date.now(),
    metadata:
      typeof action.message.metadata === "object" &&
      action.message.metadata !== null
        ? (action.message.metadata as Record<string, unknown>)
        : {},
    nodes: [
      {
        id: action.nodeId,
        name: "",
        streamParts,
        status: "completed",
        startTime: Date.now(),
        endTime: Date.now(),
      },
    ],
  };
};

// ===========================
// Node Management Helpers
// ===========================

/**
 * Finds or creates a node within a message
 */
const findOrCreateNode = (
  draft: WritableDraft<ConversationState>,
  messageIndex: number,
  nodeId: string,
  nodeName: string
): WritableDraft<ConversationNode> => {
  let nodeIndex = draft.messages[messageIndex].nodes.findIndex(
    (node) => node.id === nodeId
  );

  if (nodeIndex === -1) {
    draft.messages[messageIndex].nodes.push({
      id: nodeId,
      name: nodeName,
      streamParts: [],
    });
    nodeIndex = draft.messages[messageIndex].nodes.length - 1;
  }

  return draft.messages[messageIndex].nodes[nodeIndex];
};

/**
 * Updates node execution status in both the node and global status tracking
 */
const updateNodeStatus = (
  draft: WritableDraft<ConversationState>,
  node: WritableDraft<ConversationNode>,
  nodeId: string,
  status: "active" | "completed" | "error",
  error?: string
) => {
  const now = Date.now();

  switch (status) {
    case "active":
      node.status = "active";
      node.startTime = now;
      draft.nodeExecutionStatus[nodeId] = {
        status: "active",
        startTime: now,
      };
      break;

    case "completed":
      node.status = "completed";
      node.endTime = now;
      if (draft.nodeExecutionStatus[nodeId]) {
        draft.nodeExecutionStatus[nodeId].status = "completed";
        draft.nodeExecutionStatus[nodeId].endTime = now;
      }
      break;

    case "error":
      node.status = "error";
      node.endTime = now;
      draft.nodeExecutionStatus[nodeId] = {
        status: "error",
        error: error || "Unknown error",
        endTime: now,
      };
      break;
  }
};

// ===========================
// Action Handlers
// ===========================

const handleInitialize = (
  draft: WritableDraft<ConversationState>,
  payload: Extract<ConversationAction, { type: "initialize" }>
) => {
  Object.assign(draft, initialWorkflowConversationState);
  draft.sessionId = payload.sessionId;
  draft.workflowId = payload.workflowId;
  draft.createdAt = Date.now();
  draft.updatedAt = Date.now();
};

const handleSetStatus = (
  draft: WritableDraft<ConversationState>,
  payload: Extract<ConversationAction, { type: "setStatus" }>
) => {
  draft.status = payload.status;
  draft.updatedAt = Date.now();

  // Update streaming state based on status
  if (payload.status === "completed" || payload.status === "error") {
    draft.isStreaming = false;
  }
};

const handleAddMessage = (
  draft: WritableDraft<ConversationState>,
  payload: Extract<ConversationAction, { type: "addMessage" }>,
  hooks?: ConversationHooks
) => {
  const message = payload.message;
  if (!message) return;

  const conversationMessage = convertUIMessageToConversationMessage(
    payload,
    hooks
  );
  draft.messages.push(conversationMessage);
  draft.currentMessageId = conversationMessage.id;
  draft.updatedAt = Date.now();
  hooks?.onMessage?.(conversationMessage);
};

const handleAddNodeToMessage = (
  draft: WritableDraft<ConversationState>,
  payload: Extract<ConversationAction, { type: "addNodeToMessage" }>
) => {
  const messageIndex = draft.messages.findIndex(
    (msg) => msg.id === payload.messageId
  );

  if (messageIndex !== -1) {
    // Use findOrCreateNode helper which handles the existence check internally
    findOrCreateNode(draft, messageIndex, payload.nodeId, payload.nodeName);
    draft.updatedAt = Date.now();
  }
};

const handleAddStreamPart = (
  draft: WritableDraft<ConversationState>,
  payload: Extract<ConversationAction, { type: "addStreamPart" }>,
  hooks?: ConversationHooks
) => {
  const messageIndex = draft.messages.findIndex(
    (msg) => msg.id === payload.messageId
  );

  if (messageIndex === -1) {
    // Create new message if it doesn't exist
    draft.messages.push({
      id: payload.messageId,
      role: "assistant",
      timestamp: Date.now(),
      nodes: [
        {
          id: payload.nodeId,
          name: payload.nodeName,
          streamParts: [payload.streamPart],
        },
      ],
    });
    draft.updatedAt = Date.now();
    return;
  }

  const node = findOrCreateNode(
    draft,
    messageIndex,
    payload.nodeId,
    payload.nodeName
  );

  // Handle text concatenation
  if (isTextStreamPart(payload.streamPart)) {
    const concatenatedText = concatTextStream(
      node.streamParts,
      payload.streamPart
    );
    if (concatenatedText) {
      hooks?.onText?.(concatenatedText);
      return;
    }
  }

  // Handle tool input delta concatenation
  if (
    payload.streamPart.type === "tool-input-delta" &&
    concatToolInputDeltaStream(node.streamParts, payload.streamPart)
  ) {
    return;
  }

  // Handle tool output status update
  if (
    payload.streamPart.type === "tool-output-available" &&
    updateToolStatus(node.streamParts, payload.streamPart)
  ) {
    return;
  }

  // Handle reasoning concatenation
  if (
    payload.streamPart.type === "reasoning" &&
    concatReasoningStream(node.streamParts, payload.streamPart)
  ) {
    return;
  }

  // Prevent duplicate start/finish parts
  const lastPart = node.streamParts[node.streamParts.length - 1];
  if (
    (isStartPart(payload.streamPart) && lastPart && isStartPart(lastPart)) ||
    (isFinishPart(payload.streamPart) && lastPart && isFinishPart(lastPart))
  ) {
    return;
  }

  // Add the stream part
  node.streamParts.push(payload.streamPart);

  // Update node and workflow status based on stream part type
  switch (payload.streamPart.type) {
    case "start-step":
    case "tool-input-start":
    case "tool-input-available":
      updateNodeStatus(draft, node, payload.nodeId, "active");
      break;

    case "finish-step":
    case "tool-output-available":
      updateNodeStatus(draft, node, payload.nodeId, "completed");
      break;

    case "error":
      updateNodeStatus(
        draft,
        node,
        payload.nodeId,
        "error",
        payload.streamPart.errorText
      );
      break;

    case "workflow-start":
      draft.status = "active";
      draft.workflowStartTime = payload.streamPart.timestamp;
      draft.isStreaming = true;
      break;

    case "workflow-finish":
      draft.status = "completed";
      draft.workflowEndTime = payload.streamPart.timestamp;
      draft.isStreaming = false;
      break;

    case "start":
      draft.status = "active";
      draft.currentMessageId = payload.streamPart.messageId;
      break;

    case "finish":
      draft.updatedAt = Date.now();
      break;
  }

  draft.updatedAt = Date.now();
};

const handleSetError = (
  draft: WritableDraft<ConversationState>,
  payload: Extract<ConversationAction, { type: "setError" }>
) => {
  draft.status = "error";
  draft.error = {
    name: `Error in ${payload.nodeName}`,
    message: payload.message,
    details: {
      nodeId: payload.nodeId,
      nodeName: payload.nodeName,
      timestamp: Date.now(),
    },
  };
  draft.isStreaming = false;
  draft.updatedAt = Date.now();

  // Update node status if it exists
  if (draft.nodeExecutionStatus[payload.nodeId]) {
    draft.nodeExecutionStatus[payload.nodeId].status = "error";
    draft.nodeExecutionStatus[payload.nodeId].error = payload.message;
    draft.nodeExecutionStatus[payload.nodeId].endTime = Date.now();
  }
};

const handleSetStreaming = (
  draft: WritableDraft<ConversationState>,
  payload: Extract<ConversationAction, { type: "setStreaming" }>
) => {
  draft.isStreaming = payload.isStreaming;
  draft.updatedAt = Date.now();
};

const handleUpdateMetadata = (
  draft: WritableDraft<ConversationState>,
  payload: Extract<ConversationAction, { type: "updateMetadata" }>
) => {
  draft.metadata = {
    ...draft.metadata,
    ...payload.metadata,
  };
  draft.updatedAt = Date.now();
};

const handleStart = (draft: WritableDraft<ConversationState>) => {
  draft.status = "active";
  draft.isStreaming = true;
  draft.updatedAt = Date.now();
};

const handleFinish = (draft: WritableDraft<ConversationState>) => {
  draft.status = "completed";
  draft.isStreaming = false;
  draft.updatedAt = Date.now();
};

const handleReset = (draft: WritableDraft<ConversationState>) => {
  Object.assign(draft, initialWorkflowConversationState);
  draft.createdAt = Date.now();
  draft.updatedAt = Date.now();
};

// ===========================
// Main Reducer
// ===========================

/**
 * Workflow conversation reducer that handles all state updates
 * for the conversation flow including messages, streaming, and node execution
 */
export const workflowChatReducer = (
  draft: ConversationState,
  payload: ConversationAction,
  hooks?: ConversationHooks
) => {
  // Update streaming state for most actions (except setStatus which handles it internally)
  if (payload.type !== "setStatus" && payload.type !== "setStreaming") {
    draft.isStreaming =
      draft.status !== "completed" && draft.status !== "error";
  }

  switch (payload.type) {
    case "initialize":
      handleInitialize(draft, payload);
      break;

    case "setStatus":
      handleSetStatus(draft, payload);
      break;

    case "addMessage":
      handleAddMessage(draft, payload, hooks);
      break;

    case "addNodeToMessage":
      handleAddNodeToMessage(draft, payload);
      break;

    case "addStreamPart":
      handleAddStreamPart(draft, payload, hooks);
      break;

    case "setNextAction":
      draft.nextAction = payload.nextAction;
      draft.updatedAt = Date.now();
      break;

    case "setError":
      handleSetError(draft, payload);
      break;

    case "setStreaming":
      handleSetStreaming(draft, payload);
      break;

    case "setEventType":
      // Store event type in metadata for tracking
      draft.metadata = {
        ...draft.metadata,
        eventType: payload.eventType,
      };
      draft.updatedAt = Date.now();
      break;

    case "updateMetadata":
      handleUpdateMetadata(draft, payload);
      break;

    case "start":
      handleStart(draft);
      break;

    case "finish":
      handleFinish(draft);
      break;

    case "reset":
      handleReset(draft);
      break;

    default: {
      // Exhaustive check - if this errors, we missed a case
      const exhaustiveCheck: never = payload;
      console.warn("Unhandled action type:", exhaustiveCheck);
      break;
    }
  }

  return draft;
};
